import { BreadcrumbRouterLink } from '@/components/link/Link';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { ROUTES } from '@/constants';
import { Button } from '@/components/ui/button.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { LandOwnerFormSchema, landownerSchema } from '@/routes/landowner/zodSchemas.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import RHFDatePicker from '@/components/date-picker/RHFDatePicker.tsx';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input.tsx';
import { useLoaderData } from 'react-router';
import { deviceStatusOptions, SiteResponse } from '@/routes/landowner/types.ts';
import { AxiosResponse } from 'axios';
import { useState } from 'react';
import Spinner from '@/components/spinner/Spinner.tsx';
import { useMutation } from '@tanstack/react-query';
import { saveNewLandowner, toLandownerPayload } from '@/routes/landowner/constants.ts';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors.ts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.tsx';
import { RHFAutoComplete } from '@/components/auto-complete/RHFAutoComplete.tsx';
import useTitle from '@/hooks/useTitle.ts';

function Landowner() {
	useTitle("Landowner");

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const routeData = useLoaderData() as {
		sites: AxiosResponse<SiteResponse[]>;
	};

	const landownerForm = useForm<LandOwnerFormSchema>({
		resolver: zodResolver(landownerSchema),
		defaultValues: {
			firstName: '',
			lastName: '',
			streetAddress: '',
			city: '',
			state: '',
			zip: '',
			mailingAddress: '',
			email: '',
			phone: '',
			mobile: '',
			site: [],
			status: undefined,
			initialPayment: undefined,
			recurringPayment: undefined,
			latitude: undefined,
			longitude: undefined,
			initialDeploymentDate: undefined
		}
	});

	const { isPending: isSavingLandowner, mutate: saveLandowner } = useMutation({
		mutationFn: saveNewLandowner,
		mutationKey: ['v1', 'sites'],
		onSuccess: () => {
			landownerForm.reset();
			setShowConfirmation(false);
			toast('Landowner saved successfully');
		},
		onError: error => {
			setError(getUIErrorMessage(error));
			toast.error(getUIErrorMessage(error));
		}
	});

	const handleSaveLandowner = landownerForm.handleSubmit(values => {
		const payload = toLandownerPayload(values);
		saveLandowner(payload);
	});

	return (
		<div className="flex flex-col gap-10">
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbRouterLink to={ROUTES.home}>Home</BreadcrumbRouterLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>Landowner creation</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<Form {...landownerForm}>
				<div className="flex justify-center items-center">
					<form className="w-full max-w-4xl flex flex-col gap-10">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 items-start">
							<FormField
								control={landownerForm.control}
								name="firstName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>First Name *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="lastName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Last Name *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="streetAddress"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Street Address *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="city"
								render={({ field }) => (
									<FormItem>
										<FormLabel>City *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="state"
								render={({ field }) => (
									<FormItem>
										<FormLabel>State *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="zip"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Zip *</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="mailingAddress"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Mailing Address</FormLabel>
										<FormControl>
											<Input
												type="text"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												type="email"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="phone"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Phone *</FormLabel>
										<FormControl>
											<Input
												type="tel"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="mobile"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Mobile</FormLabel>
										<FormControl>
											<Input
												type="tel"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<RHFAutoComplete
								name={'site'}
								label="Site *"
								control={landownerForm.control}
								placeholder="Select a site"
								items={routeData.sites.data}
								isMultiSelect={false}
								itemToOption={item => ({
									value: item.id.toString(),
									label: item.name
								})}
								closeOnSelect
							/>
							<FormField
								control={landownerForm.control}
								name="status"
								render={({ field }) => (
									<FormItem>
										<FormItem>
											<FormLabel>Status *</FormLabel>

											<Select onValueChange={field.onChange} value={field.value ?? ''}>
												<FormControl>
													<SelectTrigger className="w-full">
														<SelectValue placeholder="Select a status" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{deviceStatusOptions.map(opt => (
														<SelectItem key={opt.value} value={opt.value.toString()}>
															{opt.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</FormItem>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="initialPayment"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Initial Payment *</FormLabel>
										<FormControl>
											<Input
												type="number"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="recurringPayment"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Recurring Payment *</FormLabel>
										<FormControl>
											<Input
												type="number"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="latitude"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Latitude *</FormLabel>
										<FormControl>
											<Input
												type="number"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={landownerForm.control}
								name="longitude"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Longitude *</FormLabel>
										<FormControl>
											<Input
												type="number"
												className="input"
												onChange={field.onChange}
												name={field.name}
												value={field.value || ''}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<RHFDatePicker
								control={landownerForm.control}
								name="initialDeploymentDate"
								label="Initial deployment date *"
								placeholder="Pick a date"
								isFullWidth={true}
							/>
						</div>
						{!showConfirmation ? (
							<Button type="submit" onClick={() => setShowConfirmation(true)}>
								Save landowner
							</Button>
						) : (
							<div className="flex flex-col gap-2 text-sm justify-center items-center">
								<p>Are you sure you want to save this landowner?</p>
								<div className="flex gap-2 w-full justify-center">
									<Button
										className="flex-1"
										variant="secondary"
										onClick={() => {
											setShowConfirmation(false);
											setError('');
										}}
										disabled={isSavingLandowner}
									>
										Cancel
									</Button>
									<Button
										className="flex-1"
										type="submit"
										onClick={handleSaveLandowner}
										disabled={isSavingLandowner}
									>
										{error ? 'Try again ' : 'Save'}
										{isSavingLandowner && <Spinner className="ml-2" />}
									</Button>
								</div>
							</div>
						)}
					</form>
				</div>
			</Form>
		</div>
	);
}

export default Landowner;
